import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex items-center gap-2" };
const _hoisted_2 = ["id"];
const _hoisted_3 = { class: "flex flex-col [&>*:last-child]:mb-0" };
const _hoisted_4 = { class: "flex flex-col gap-1 mb-5" };
const _hoisted_5 = { class: "text-sm font-medium leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_6 = { class: "flex items-center gap-4" };
const _hoisted_7 = {
    key: 0,
    class: "rounded-lg bg-(--background-background-error) border border-(--border-border-error-subtle) px-4 py-3 text-sm text-(--text-text-error) flex items-center gap-2"
};
const _hoisted_8 = { class: "flex w-full items-center justify-between" };
const _hoisted_9 = { class: "flex items-center gap-4" };
import { computed, reactive, ref, watch, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Modal, PhoneInput, RadioButton, ToggleInput, createPhoneValidator, PHONE_FORMATS_BY_DIAL_CODE, significantDigitCount, withHeuristicTrunkZero, } from '@eudonet/opal';
import { toStoredPhoneValue } from '@/shared/composables/channels/useAddPhoneForm';
import { useAppToast } from '@/shared/composables/useAppToast';
import { resolveApiErrorMessage } from '@/infrastructure/services/ApiErrorService';
import { logger } from '@/infrastructure/services/LoggerService';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EditPhoneModal',
    props: /*@__PURE__*/ _mergeModels({
        initialValue: {},
        initialIsPrimary: { type: Boolean },
        initialSubType: { default: 'Mobile' },
        submit: {},
        i18nPrefix: {},
        fieldI18nPrefix: {},
        errorOverrides: { default: () => ({}) }
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["updated", "delete"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        /**
         * Reusable "Éditer un numéro" modal (OP-5611 / US-15).
         *
         * Mirrors {@link EditEmailModal} in look & feel and {@link AddPhoneModal} for the phone field, but
         * restricted to the editable fields of an existing channel: the value, the Mobile/Fixe subtype and
         * the primary flag. Context (Personal/Professional) and the organisation anchor are NOT editable
         * (out of scope — delete + recreate via OP-5604), so they are preserved server-side by the BFF
         * read-merge-write passthrough and never shown here.
         *
         * Owner-agnostic: the persistence call (`submit`) and the i18n namespaces are injected. `i18nPrefix`
         * resolves the edit-only keys (`title`, `delete`, `saveSuccess`, `errors.serverError`); `fieldI18nPrefix`
         * reuses the existing "Ajouter un numéro" field labels (`phone.*`, `type.*`, `primary`, `actions.*`,
         * `validation.*`) so the two modals stay visually and textually consistent without duplicating keys.
         */
        const props = __props;
        const open = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t, locale } = useI18n();
        const tkOwn = (key) => t(`${props.i18nPrefix}.${key}`);
        const tkField = (key) => t(`${props.fieldI18nPrefix}.${key}`);
        const { toastSuccess } = useAppToast();
        const titleId = useId();
        const phone = ref(withHeuristicTrunkZero(props.initialValue));
        const subType = ref(props.initialSubType);
        const isPrimary = ref(props.initialIsPrimary);
        const submitting = ref(false);
        const serverError = ref(null);
        // True once the user has actually edited the field (typed a digit or switched country). Before that
        // we trust the pre-filled, already-persisted value as-is: OPAL PhoneInput parses the initial value
        // but only emits `country-change` (which seeds `phoneMeta`) AFTER an interaction, so on a freshly
        // opened modal there is nothing to validate against and re-opening just to flip the primary/subtype
        // must not be blocked.
        const phoneInteracted = ref(false);
        // Mirrors OPAL PhoneInput's internal split (dial code + national digits). Seeded from the
        // `country-change` event the input emits on init and on every keystroke.
        const phoneMeta = reactive({ dialCode: '', nationalNumber: '' });
        // Re-seed the form whenever the modal (re)opens so it always reflects the latest channel data.
        watch(open, (isOpen) => {
            if (isOpen) {
                phone.value = withHeuristicTrunkZero(props.initialValue);
                subType.value = props.initialSubType;
                isPrimary.value = props.initialIsPrimary;
                phoneMeta.dialCode = '';
                phoneMeta.nationalNumber = '';
                phoneInteracted.value = false;
                serverError.value = null;
            }
        });
        /**
         * National-significant digits, for VALIDATION counting only. Delegates to OPAL's
         * `significantDigitCount`, the single source of truth shared with PhoneInput's own validation,
         * {@link toStoredPhoneValue}, and every other add/edit surface.
         */
        const significantNationalDigits = computed(() => significantDigitCount(phoneMeta.nationalNumber.replace(/\D/g, ''), PHONE_FORMATS_BY_DIAL_CODE[phoneMeta.dialCode]?.hasTrunkZero ?? false));
        // Per-country digit validator (FR = exactly 9 digits, …). Returns the localized message via the
        // shared `errors.phoneDigits` key, or undefined when the count matches / the field is empty.
        // CONSTRAINT: pass the RAW digits, not `significantNationalDigits` — createPhoneValidator
        // applies its own single significantDigitCount strip internally. Passing an already-stripped
        // value here double-strips: a normal single-trunk-zero entry is unaffected (idempotent), but an
        // over-typed extra leading zero (e.g. FR "00612345678") would silently validate as 9 digits
        // instead of correctly failing at 10.
        const phoneDigitError = createPhoneValidator(() => phoneMeta.nationalNumber.replace(/\D/g, ''), () => phoneMeta.dialCode, (params) => t('errors.phoneDigits', params));
        /** True once the user has typed at least one significant national digit. */
        const hasNationalDigits = computed(() => significantNationalDigits.value.length > 0);
        // Blocking format error (OP-6096). Shown as soon as there is input and the per-country digit count
        // is off, and it gates submission via `canSubmit` — an invalid number must never reach the API.
        const phoneError = computed(() => (hasNationalDigits.value ? phoneDigitError(null) ?? '' : ''));
        // "Enregistrer" gating (OP-6096 — a malformed number must not be saveable and must not trigger an
        // API call). Once the user has edited the field, require significant national digits whose count
        // matches the selected country. Before any interaction we trust the pre-filled value (already
        // persisted, hence valid): OPAL PhoneInput only seeds `phoneMeta` after an interaction, so on a
        // freshly opened modal we fall back to the displayed pre-filled string here — `handleSubmit`
        // falls back to `initialValue` itself (see its own comment) so the payload stays E.164.
        const isPhoneValid = computed(() => phoneInteracted.value
            ? hasNationalDigits.value && !phoneDigitError(null)
            : phone.value.trim().length > 0);
        const canSubmit = computed(() => isPhoneValid.value);
        function setPhoneMeta(dialCode, nationalNumber) {
            // `country-change` only fires after a real user interaction (typing a digit / switching country),
            // never for PhoneInput's initial parse — so reaching here means the field was edited.
            phoneInteracted.value = true;
            phoneMeta.dialCode = dialCode;
            phoneMeta.nationalNumber = nationalNumber;
        }
        function onPhoneBlur() {
            // Collapse separator noise as soon as the user leaves the field — NEVER the trunk zero typed.
            // `phone` is PhoneInput's v-model: writing the E.164-stripped form back into it made PhoneInput's
            // own modelValue watcher detect a mismatch and re-parse, visibly dropping the "0" the instant the
            // user tabbed out — a regression against OP-7665 AC3 (the field keeps showing exactly what was
            // typed, 0 or not, for the whole editing session, not just live keystrokes). Stripping to E.164
            // for the WIRE value is handleSubmit's job alone, via toStoredPhoneValue. No-op while empty.
            const digits = phoneMeta.nationalNumber.replace(/\D/g, '');
            if (digits && /[1-9]/.test(digits)) {
                phone.value = `+${phoneMeta.dialCode.replace(/\D/g, '')}${digits}`;
            }
        }
        async function handleSubmit() {
            if (!canSubmit.value)
                return;
            submitting.value = true;
            serverError.value = null;
            try {
                // Defense-in-depth: re-normalize in case submit fires without a blur (e.g. Enter key). When
                // PhoneInput never emitted country meta (field untouched — see `isPhoneValid`), fall back to
                // `initialValue` (already strict E.164), NOT `phone.value` — the field displays
                // `withHeuristicTrunkZero(initialValue)`, which re-adds the trunk zero for the UI only,
                // and resending it verbatim would round-trip that zero straight back into storage (OP-7665).
                const value = toStoredPhoneValue(phoneMeta.dialCode, phoneMeta.nationalNumber) || props.initialValue;
                await props.submit({ value, isPrimary: isPrimary.value, subType: subType.value });
                emit('updated');
                open.value = false;
                toastSuccess(`${props.i18nPrefix}.saveSuccess`);
            }
            catch (error) {
                serverError.value = resolveApiErrorMessage(error, props.errorOverrides, `${props.i18nPrefix}.errors.serverError`);
                logger.error('Failed to update phone', error);
            }
            finally {
                submitting.value = false;
            }
        }
        function handleDelete() {
            emit('delete');
        }
        function handleCancel() {
            serverError.value = null;
            open.value = false;
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: open.value,
                "onUpdate:modelValue": [
                    _cache[5] || (_cache[5] = ($event) => ((open).value = $event)),
                    _cache[6] || (_cache[6] = (v) => !v && handleCancel())
                ],
                size: "sm",
                "show-close-button": true,
                "show-header-border": false,
                "show-footer-border": false,
                "aria-labelledby": _unref(titleId)
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _cache[7] || (_cache[7] = _createElementVNode("span", {
                            class: "flex h-9 items-center justify-center rounded-lg border border-(--border-border-base-soft) px-3 dark:border-(--border-border-base)",
                            "aria-hidden": "true"
                        }, [
                            _createElementVNode("i", { class: "ri-phone-fill text-xl text-(--text-text-black-default) dark:text-(--text-text-white-default)" })
                        ], -1 /* CACHED */)),
                        _createElementVNode("h2", {
                            id: _unref(titleId),
                            class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                        }, _toDisplayString(tkOwn('title')), 9 /* TEXT, PROPS */, _hoisted_2)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createElementVNode("div", _hoisted_8, [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: tkField('actions.cancel'),
                            onClick: handleCancel
                        }, null, 8 /* PROPS */, ["label"]),
                        _createElementVNode("div", _hoisted_9, [
                            _createVNode(_unref(Button), {
                                variant: "error-secondary",
                                label: tkOwn('delete'),
                                "icon-left": "ri-delete-bin-line",
                                onClick: handleDelete
                            }, null, 8 /* PROPS */, ["label"]),
                            _createVNode(_unref(Button), {
                                variant: "primary",
                                label: tkField('actions.submit'),
                                disabled: !canSubmit.value || submitting.value,
                                loading: submitting.value,
                                onClick: handleSubmit
                            }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                        ])
                    ])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_3, [
                        _createVNode(_unref(PhoneInput), {
                            modelValue: phone.value,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((phone).value = $event)),
                            label: tkField('phone.label'),
                            "country-list-label": _unref(t)('common.country'),
                            locale: _unref(locale),
                            error: phoneError.value,
                            required: true,
                            "disable-internal-validation": true,
                            "full-width": "",
                            onBlur: onPhoneBlur,
                            onCountryChange: _cache[1] || (_cache[1] = (country, national) => setPhoneMeta(country.dialCode, national))
                        }, null, 8 /* PROPS */, ["modelValue", "label", "country-list-label", "locale", "error"]),
                        _createElementVNode("fieldset", _hoisted_4, [
                            _createElementVNode("legend", _hoisted_5, [
                                _createTextVNode(_toDisplayString(tkField('type.label')) + " ", 1 /* TEXT */),
                                _cache[8] || (_cache[8] = _createElementVNode("span", { "aria-hidden": "true" }, "*", -1 /* CACHED */))
                            ]),
                            _createElementVNode("div", _hoisted_6, [
                                _createVNode(_unref(RadioButton), {
                                    modelValue: subType.value,
                                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((subType).value = $event)),
                                    value: 'Mobile',
                                    name: "edit-phone-subtype",
                                    label: tkField('type.mobile')
                                }, null, 8 /* PROPS */, ["modelValue", "label"]),
                                _createVNode(_unref(RadioButton), {
                                    modelValue: subType.value,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((subType).value = $event)),
                                    value: 'Fixe',
                                    name: "edit-phone-subtype",
                                    label: tkField('type.fixe')
                                }, null, 8 /* PROPS */, ["modelValue", "label"])
                            ])
                        ]),
                        _createVNode(_unref(ToggleInput), {
                            modelValue: isPrimary.value,
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => ((isPrimary).value = $event)),
                            type: "default",
                            class: "mb-5",
                            label: tkField('primary'),
                            disabled: __props.initialIsPrimary
                        }, null, 8 /* PROPS */, ["modelValue", "label", "disabled"]),
                        (serverError.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _cache[9] || (_cache[9] = _createElementVNode("i", {
                                    class: "ri-error-warning-line text-base shrink-0",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createTextVNode(" " + _toDisplayString(serverError.value), 1 /* TEXT */)
                            ]))
                            : _createCommentVNode("v-if", true)
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "aria-labelledby"]));
        };
    }
});
